// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Tools Menu UI — the Browse, Tools and Fullscreen buttons, and the Tools
 * panel that opens above them.
 *
 * AYNI: the panel is AYNI's own (docs/AYNI_TOOLS_DATA_PLAN.md, section 1):
 *
 *   [▦ Browse] [⚙ Tools] [⛶]          ← icon buttons, always visible
 *
 *   ┌─ Tools ──────────────────── ✕ ┐
 *   │ [ Start a meeting ]           │  ← when this browser can host
 *   │ Layers │ Data │ View │ More   │  ← tabs (the last one is remembered)
 *   │  Layers: basemap / overlay / real time (mapLayersUI); borders
 *   │          are an overlay, automatic on opaque streams
 *   │  Data:   pins and graph (dataPinsUI); line, box and region
 *   │          statistics (analyzeUI); clear everything on the globe
 *   │  View:   palette, invert, colour limits (viewPaletteUI),
 *   │          layout, sun glint; a closed legend (legendDock)
 *   │  More:   labels, terrain, info, legend, UI size,
 *   │          language, credits, privacy, about
 *   │  (Share is the home screen's, under Show anchor: shareButton;
 *   │  auto-rotate is a button under Help: autoRotateControl)
 *   │ Plan: Teacher · Your plan     │  ← the account's plan, or Free
 *   └───────────────────────────────┘
 *
 * Every control keeps the id it always had, so wiring and tests do not
 * depend on which tab it sits in.
 *
 * Toggle actions fan out across every viewport in the current
 * ViewportManager so overlay state stays synchronised across panels.
 * The Browse and Tools buttons sit on top of MapLibre, so they
 * receive clicks before the map. The popover uses `pointer-events:
 * auto` on itself and the map-grid remains the only thing under
 * `pointer-events: none` regions.
 */

import type { ViewportManager, ViewLayout } from '../services/viewportManager'
import { updateMapControlsPosition } from './mapControlsUI'
import { openPrivacyUI } from './privacyUI'
import { ANALYZE_ZONES, clearAnalysis, closeAnalyzeUI, isAnalyzeUIOpen, openAnalyzeScoped, setAnalyzeTab, startAnalyzeBox, startAnalyzeLine, type AnalyzeScope, type AnalyzeZoneId } from './analyzeUI'
import { emit } from '../analytics'
import { maxVideoPanels } from '../utils/deviceCapability'
import { dataToolsAllowed, lineBoxLocked, maxGlobes, regionStatsLocked, paletteAllowed, teachingPlan } from '../services/planAccess'
import { openPlans } from './xrPlansUI'
import type { FullscreenController } from '../services/windowChrome'
import {
  loadUiScale,
  nearestPreset,
  setUiScale,
  UI_SCALE_PRESETS,
  type UiScalePreset,
} from '../services/uiScaleService'
import {
  getShaderSettings,
  matchSpecularPreset,
  setSpecularPreset,
  SPECULAR_PRESETS,
  type SpecularPreset,
} from '../services/shaderSettingsService'
import {
  getLocale,
  NATIVE_NAMES,
  PICKER_LOCALES,
  t,
  tAttr,
  tHtml,
  type Locale,
  type MessageKey,
} from '../i18n'
import { saveLocalePref } from '../i18n/persistence'
import { escapeAttr, escapeHtml } from './domUtils'

/** AYNI: the Tools panel's tabs. */
export type ToolsTab = 'layers' | 'data' | 'view' | 'more'
const TOOLS_TABS: readonly ToolsTab[] = ['layers', 'data', 'view', 'more']
/** The tab the panel opens on, remembered per browser (a convenience; nothing depends on it). */
const TAB_STORAGE_KEY = 'ayni-tools-tab'

function storedTab(): ToolsTab {
  try {
    const v = localStorage.getItem(TAB_STORAGE_KEY) as ToolsTab | null
    return v && TOOLS_TABS.includes(v) ? v : 'layers'
  } catch {
    return 'layers'
  }
}

/** Line icons for the corner buttons, drawn in the text colour (replacing terraviz's emoji). */
const svg = (body: string): string =>
  `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false">${body}</svg>`
const ICON_BROWSE = svg('<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>')
const ICON_TOOLS = svg('<line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/><circle cx="9" cy="6" r="2" fill="currentColor"/><circle cx="15" cy="12" r="2" fill="currentColor"/><circle cx="7" cy="18" r="2" fill="currentColor"/>')
/** AYNI: Solar System — the Sun, an orbit and a planet on it. */
const ICON_SOLAR = svg('<circle cx="12" cy="12" r="3.2" fill="currentColor"/><ellipse cx="12" cy="12" rx="10" ry="4.6" transform="rotate(-22 12 12)"/><circle cx="20.2" cy="8.7" r="1.7" fill="currentColor" stroke="none"/>')
/** AYNI: Analyze — bars under a trend line. */
const ICON_ANALYZE = svg('<line x1="4" y1="20" x2="20" y2="20"/><rect x="5.5" y="13" width="3" height="5" rx="0.5"/><rect x="10.5" y="9" width="3" height="9" rx="0.5"/><rect x="15.5" y="11" width="3" height="7" rx="0.5"/><path d="M4 9l5-4 4 3 7-5"/>')
/** AYNI Studio: a film frame. */
const ICON_STUDIO = svg('<rect x="3" y="6" width="14" height="12" rx="1.5"/><path d="M17 10l4-2v8l-4-2"/>')
const ICON_FULLSCREEN = svg('<path d="M4 9V4h5"/><path d="M20 9V4h-5"/><path d="M4 15v5h5"/><path d="M20 15v5h-5"/>')

/** Fire a `settings_changed` event for a toggle/action in the Tools
 * popover. `key` is the logical name (labels / borders / etc.) and
 * `value_class` is a short label describing the new value — for
 * booleans this is "on" / "off", for categorical values it's the
 * value itself. Never carries user data. */
function emitSetting(key: string, valueClass: string): void {
  emit({ event_type: 'settings_changed', key, value_class: valueClass })
}

/** True when the browser is currently rendering some element
 *  fullscreen. Wraps `document.fullscreenElement` (which some
 *  test environments leave as `undefined` rather than `null`) so
 *  the rest of the file doesn't have to repeat the falsy check. */
function isFullscreen(): boolean {
  // The controller first when one is wired (main.ts wires the DOM one).
  return controller ? controller.isFullscreen() : Boolean(document.fullscreenElement)
}

/**
 * The window-chrome controller, once a host has wired one.
 *
 * Module-scoped rather than threaded through, because
 * `syncFullscreenButton` is called from a `fullscreenchange` listener
 * registered once for the life of the document — it has no closure to
 * read from, and giving it one would mean re-registering that listener
 * on every re-init.
 */
let controller: FullscreenController | null = null

/** Toggle the document into / out of fullscreen via the standard
 *  Fullscreen API. Errors (autoplay-policy denial, browser
 *  feature-policy block) are swallowed silently — the button keeps
 *  its current state and the `fullscreenchange` event never fires,
 *  so `syncFullscreenButton` doesn't have anything to do. */
async function toggleFullscreen(): Promise<void> {
  // The controller already absorbs a refusal and reports what the
  // window actually is, so there is nothing to catch around it.
  if (controller) {
    await controller.toggle()
    return
  }
  try {
    if (isFullscreen()) {
      await document.exitFullscreen()
    } else {
      await document.documentElement.requestFullscreen()
    }
  } catch {
    // Silent — the request was denied (autoplay policy, top-level
    // browsing context, Permissions Policy) and the icon stays
    // where it was.
  }
}

/** Mirror the current fullscreen state into the toolbar button's
 *  icon, tooltip, ARIA label, and `aria-pressed`. Called once at
 *  init and again whenever `fullscreenchange` fires. */
function syncFullscreenButton(): void {
  const btn = document.getElementById('tools-menu-fullscreen')
  if (!btn) return
  const on = isFullscreen()
  const label = on
    ? t('mapControls.fullscreen.exit')
    : t('mapControls.fullscreen.enter')
  btn.setAttribute('title', label)
  btn.setAttribute('aria-label', label)
  btn.setAttribute('aria-pressed', on ? 'true' : 'false')
  // The glyph (U+26F6 SQUARED FOUR CORNERS, text style) stays the
  // same in both states — the active background + aria-pressed +
  // accessible label convey the transition. Pairing it with a
  // distinct "exit fullscreen" glyph would require choosing one of
  // the ambiguous diagonal-arrow codepoints, none of which read as
  // clearly as a label change.
}

/** Callbacks the tools menu fires out into the rest of the app. */
export interface ToolsMenuCallbacks {
  /** Multi-viewport: user picked a layout from the picker. */
  onSetLayout?: (layout: ViewLayout) => void
  /** User clicked Browse — open the dataset list. */
  onOpenBrowse?: () => void
  /** AYNI (owner, 2026-11-04): the Solar System button beside Browse (solarSystemMode.ts). */
  onOpenSolar?: () => void
  /** User toggled dataset info visibility. */
  onToggleDatasetInfo?: (visible: boolean) => void
  /** User toggled legend visibility. */
  onToggleLegend?: (visible: boolean) => void
  /**
   * The window's fullscreen state (`docs/MULTI_MONITOR_PLAN.md` §3.6).
   *
   * The toolbar button below predates this and drove
   * `document.requestFullscreen` directly. That covers a browser and is
   * half the answer in a packaged app: it makes the *webview*
   * fullscreen while leaving the native title bar and border in place,
   * both of which land in the signal when an operator captures the
   * control display. When a controller is supplied it owns the toggle
   * instead, pairing fullscreen with `setDecorations` and persisting
   * the choice; without one the old path stands, which is what the web
   * build still uses.
   */
  fullscreen?: FullscreenController
  /** User clicked Credits — open the credits / attribution
   *  dialog. The Tools menu hands its always-visible toggle
   *  button as `trigger` so the credits panel can restore focus
   *  there when it closes (the menu item itself is hidden by
   *  closePopover() before the dialog opens, so it isn't a
   *  reliable focus target). */
  onOpenCredits?: (trigger: HTMLElement) => void
  /**
   * AYNI (multi-globe plan, phase 4): open an output window — the globes
   * alone, for a projector. Absent renders no entry. Returns whether the
   * window opened or the browser blocked it.
   */
  onOpenOutput?: () => 'opened' | 'blocked'
  /** AYNI (multi-globe plan, phase 5): an output window that is a Science On a Sphere feed (a 2:1 picture). */
  onOpenSphereOutput?: () => 'opened' | 'blocked'
  /**
   * AYNI: user clicked Start a meeting. Absent renders no entry — the
   * callback's presence is the gate, as with Credits; while
   * present, `meetingAvailable` decides on each open whether it shows.
   */
  onOpenMeeting?: (trigger: HTMLElement) => void
  /**
   * AYNI: asked each time the menu opens; false hides the meeting entry.
   * A licence that hosts can arrive or end while the page is open
   * (`xrLicence.ts`), so whether to offer the entry is not decided once.
   * Absent: shown whenever `onOpenMeeting` is.
   */
  meetingAvailable?: () => boolean
  /**
   * AYNI (Situation room, 2026-10-11): open the Situation room's panel.
   * Absent renders no entry; `situationAvailable` decides on each open
   * whether it shows (the Situation Room plans).
   */
  onOpenSituation?: (trigger: HTMLElement) => void
  situationAvailable?: () => boolean
  /**
   * AYNI: the line at the foot of the panel saying what this account's
   * plan is, with one button (Your plan, or See the plans). Asked on each
   * open. Absent: no line.
   */
  planLine?: () => { text: string; action: string; onClick: (trigger: HTMLElement) => void }
  /** Announce something for screen readers. */
  announce?: (message: string) => void
  /** AYNI: lift every Data-tab pin (the Data tab's Clear). */
  onClearPins?: () => void
  /**
   * AYNI: why Line, Box and Statistics have nothing to measure (no dataset,
   * or a picture), or null when they can. Asked when the panel opens and
   * when the Data tab is shown; the note replaces nothing, the buttons stay.
   */
  measureNote?: () => string | null
  /** The currently loaded dataset. AYNI: unused here since Share moved to
   *  the home screen (shareButton.ts); kept so existing callers compile. */
  getCurrentDataset?: () => { id: string; title: string; slug?: string } | null
}

/** Open/close state for the popover. Tracked here because DOM tests
 *  need a deterministic way to introspect it. */
let isOpen = false
/** AYNI: whether to offer Start a meeting, asked on each open (`meetingAvailable`). */
let meetingAvailable: (() => boolean) | undefined
/** AYNI: whether to offer the Situation room, asked on each open (`situationAvailable`). */
let situationAvailable: (() => boolean) | undefined
/** AYNI: the plan line's contents, asked on each open (`planLine`). */
let planLine: ToolsMenuCallbacks['planLine']
/** AYNI: the Measure section's note, asked on each open and tab change (`measureNote`). */
let measureNote: ToolsMenuCallbacks['measureNote']

/** AYNI: show or hide the Measure section's note. */
function renderMeasureNote(): void {
  const el = document.getElementById('tools-menu-measure-note')
  if (!el) return
  const note = measureNote?.() ?? null
  el.hidden = !note
  el.textContent = note ?? ''
}

/** The layouts a plan can lock, with their globe count and their usual label. */
const PLAN_LAYOUTS: ReadonlyArray<readonly [ViewLayout, number, MessageKey]> = [
  ['2h', 2, 'tools.layout.twoHorizontal'],
  ['2v', 2, 'tools.layout.twoVertical'],
  ['4', 4, 'tools.layout.four'],
]

/**
 * AYNI: what the account's plan locks in the panel, asked on each open
 * (owner, 2026-10-05): layouts wider than the plan's globes (Education: 2),
 * which open the plans when pressed, and whole-region statistics, replaced
 * by a line saying where they are. A layout the device cannot hold keeps
 * the device's reason.
 */
function renderPlanGates(): void {
  const globes = maxGlobes()
  // AYNI (owner, 2026-10-17): with one globe (Free, Plus) the View tab has no
  // layout row, and the colour palette is for the paid plans but Plus.
  const layoutSection = document.getElementById('tools-view-layout')
  if (layoutSection) layoutSection.hidden = globes <= 1
  const paletteSection = document.getElementById('tools-view-palette')
  if (paletteSection) paletteSection.hidden = !paletteAllowed()
  for (const [layout, count, label] of PLAN_LAYOUTS) {
    const btn = document.getElementById(`tools-menu-layout-${layout}`)
    if (!btn) continue
    const deviceLocked = layout === '4' && maxVideoPanels() < 4
    const planLocked = !deviceLocked && count > globes
    const text = deviceLocked ? t('tools.layout.four.unavailable') : planLocked ? t('tools.layout.planLocked') : t(label)
    if (deviceLocked || planLocked) btn.setAttribute('aria-disabled', 'true')
    else btn.removeAttribute('aria-disabled')
    btn.dataset.planLock = planLocked ? '1' : ''
    btn.title = text
    btn.setAttribute('aria-label', text)
  }
  const statsLocked = regionStatsLocked()
  const statsRow = document.querySelector<HTMLElement>('.tools-measure-stats')
  if (statsRow) statsRow.hidden = statsLocked
  const statsNote = document.getElementById('tools-menu-stats-plan')
  if (statsNote) statsNote.hidden = !statsLocked
  // AYNI (owner, 2026-10-06): Plus measures values and pins, not Line and
  // Box; the buttons stay so their reason is reachable, and open the plans.
  const measureLocked = lineBoxLocked()
  for (const id of ['tools-menu-line', 'tools-menu-box']) {
    const btn = document.getElementById(id)
    if (!btn) continue
    btn.dataset.planLock = measureLocked ? '1' : ''
    if (measureLocked) btn.title = t('tools.measure.lineBoxPlan')
    else btn.removeAttribute('title')
  }
}

/** AYNI: show one tab without opening the panel (a lent class sees the Data tab only). */
export function showToolsTab(tab: ToolsTab): void {
  selectTab(tab)
}

/** AYNI: show one tab of the panel, and remember it. */
function selectTab(next: ToolsTab, focus = false): void {
  for (const id of TOOLS_TABS) {
    const tabBtn = document.getElementById(`tools-menu-tab-${id}`)
    const panel = document.getElementById(`tools-menu-panel-${id}`)
    const on = id === next
    tabBtn?.setAttribute('aria-selected', String(on))
    tabBtn?.setAttribute('tabindex', on ? '0' : '-1')
    tabBtn?.classList.toggle('active', on)
    if (panel) panel.hidden = !on
    if (on && focus) tabBtn?.focus()
  }
  if (next === 'data') renderMeasureNote()
  try {
    localStorage.setItem(TAB_STORAGE_KEY, next)
  } catch {
    // storage refused: the next open starts on Layers
  }
}

/** AYNI: fill the plan line from `planLine`, when there is one. */
function renderPlanLine(): void {
  const line = planLine?.()
  const text = document.getElementById('tools-menu-plan-text')
  const btn = document.getElementById('tools-menu-plan-btn')
  if (!line || !text || !btn) return
  text.textContent = line.text
  btn.textContent = line.action
}

/** AYNI: the region picked beside Statistics, as Analyze's scope. */
function statsScope(): AnalyzeScope {
  const v = document.querySelector<HTMLSelectElement>('#tools-menu-stats-region')?.value ?? 'dataset'
  return v === 'view' ? { kind: 'view' }
    : v.startsWith('zone:') ? { kind: 'zone', id: v.slice(5) as AnalyzeZoneId }
      : { kind: 'dataset' }
}

/**
 * AYNI (owner, 2026-10-06): the bar's Analyze button, shown where whole-region
 * statistics are in the plan (Research, Media, Situation) and the dataset has
 * values to measure; pressed while the panel is open. Not for Education
 * (owner, 2026-10-17): teachers reach the same tools from the Tools tab.
 * Cheap, so main.ts calls it on its 200 ms tick, which follows dataset loads,
 * plan changes and closes alike.
 */
export function syncAnalyzeButton(): void {
  const btn = document.getElementById('tools-bar-analyze')
  if (!btn) return
  const show = dataToolsAllowed() && !regionStatsLocked() && !teachingPlan() && measureNote?.() === null
  if (btn.hidden === show) btn.hidden = !show
  const open = String(isAnalyzeUIOpen())
  if (btn.getAttribute('aria-pressed') !== open) btn.setAttribute('aria-pressed', open)
}

/** Open the Tools panel on a tab, from elsewhere in the app. */
export function openToolsTab(tab: ToolsTab): void {
  if (!isOpen) openPopover()
  selectTab(tab)
}

/** Set up the tools menu inside `#map-controls`, replacing whatever
 *  is there. Idempotent — re-calling rebuilds the DOM so init can be
 *  driven from tests as well as the app boot path. */
export function initToolsMenu(
  viewports: ViewportManager,
  callbacks: ToolsMenuCallbacks = { getCurrentDataset: () => null },
): void {
  const container = document.getElementById('map-controls')
  if (!container) return

  // Reset open state whenever we rebuild — matters for tests that
  // call initToolsMenu multiple times and for main.ts init flows
  // that re-run on hot-reload. Without this the module-level flag
  // leaks between invocations.
  isOpen = false

  const { onSetLayout, onOpenBrowse, onOpenSolar, onToggleDatasetInfo, onToggleLegend, onOpenCredits, onOpenOutput, onOpenSphereOutput, onOpenMeeting, onOpenSituation, announce } = callbacks
  meetingAvailable = callbacks.meetingAvailable
  situationAvailable = callbacks.situationAvailable
  planLine = callbacks.planLine
  measureNote = callbacks.measureNote
  const currentLayout = viewports.getLayout()
  // A phone cannot hold four video decoders — the third crashes the tab
  // while still loading (terraviz#230) — so the option is turned off
  // rather than offered and then quietly reduced under the user.
  const fourGlobeAllowed = maxVideoPanels() >= 4

  // Resolve the current UI scale (precedence: localStorage → env →
  // 1.0) so the radio's initial active button matches what's
  // already applied to :root. initUiScale() ran at module-eval in
  // main.ts; we only read the current value here. We use
  // `nearestPreset()` rather than `matchPreset()` so a non-preset
  // value (e.g. VITE_DEFAULT_UI_SCALE=1.25 or a hand-edited
  // localStorage entry) still highlights *some* button — an
  // unselected button group is confusing UX.
  const currentUiScalePreset = nearestPreset(loadUiScale())

  // Resolve the current specular preset (§7.2). matchSpecularPreset
  // returns null when the live value is between presets — the
  // ?tune=shader page can write any value — so we fall back to
  // 'default' for the highlight rather than leaving the radio
  // unselected. Same UX reasoning as the UI-scale row above.
  const currentSpecular = matchSpecularPreset(getShaderSettings().specularStrength)
    ?? 'default'

  const activeLocale = getLocale()
  // Only show locales that have crossed the picker-visibility
  // coverage gate (PICKER_LOCALES, gated at ≥80% by the codegen).
  // Always include the currently-active locale even if it's below
  // threshold, so a user who landed via `?lang=ar` (or has a
  // stored pref for a below-threshold locale) can see their
  // active selection in the dropdown and switch out of it.
  // Both BCP-47 tags (validated at codegen time against ^[a-z]…$)
  // and the curated NATIVE_NAMES are trusted today, but escape
  // anyway — defense in depth keeps this safe if either source is
  // ever extended to translator input or runtime overrides (see
  // L2 partner-overrides plan in docs/archive/terraviz/I18N_PLAN.md).
  const visibleLocales: readonly Locale[] = PICKER_LOCALES.includes(activeLocale)
    ? PICKER_LOCALES
    : [...PICKER_LOCALES, activeLocale]
  const localeOptions = visibleLocales
    .map((l) => `<option value="${escapeAttr(l)}"${l === activeLocale ? ' selected' : ''}>${escapeHtml(NATIVE_NAMES[l] ?? l)}</option>`)
    .join('')

  container.classList.remove('hidden')
  container.classList.add('tools-menu-host')
  // Every translated string flows through tHtml/tAttr because
  // these blobs land directly in innerHTML — translator content
  // is translator input (treat as untrusted) and the build-time forbidden-
  // pattern gate catches script-class HTML but not e.g. <img src>
  // or attribute breakouts. tHtml escapes for element text;
  // tAttr escapes for quoted attribute values.
  // AYNI: the panel is tabbed — Layers · Data · View · More
  // (docs/AYNI_TOOLS_DATA_PLAN.md, section 1). Every control keeps its id,
  // so the wiring below and the tests do not depend on where it sits.
  const tab = (id: ToolsTab, key: MessageKey) =>
    `<button type="button" role="tab" class="tools-menu-tab" id="tools-menu-tab-${id}" aria-controls="tools-menu-panel-${id}" aria-selected="false" tabindex="-1" data-tab="${id}">${tHtml(key)}</button>`
  const item = (id: string, key: MessageKey, extra = '') =>
    `<button type="button" class="tools-menu-item" id="${id}"${extra}>
          <span class="tools-menu-item-check" aria-hidden="true"></span>
          <span class="tools-menu-item-label">${tHtml(key)}</span>
        </button>`
  container.innerHTML = `
    <button type="button" class="tools-menu-btn tools-menu-browse" id="tools-menu-browse" title="${tAttr('tools.browse.aria')}" aria-label="${tAttr('tools.browse.aria')}">
      <span class="tools-menu-btn-icon" aria-hidden="true">${ICON_BROWSE}</span>
      <span class="tools-menu-btn-label">${tHtml('tools.browse.label')}</span>
    </button>
    <button type="button" class="tools-menu-btn tools-menu-solar" id="tools-menu-solar" title="${tAttr('solar.button.aria')}" aria-label="${tAttr('solar.button.aria')}" aria-pressed="false"${callbacks.onOpenSolar ? '' : ' hidden'}>
      <span class="tools-menu-btn-icon" aria-hidden="true">${ICON_SOLAR}</span>
      <span class="tools-menu-btn-label">${tHtml('solar.button.label')}</span>
    </button>
    <button type="button" class="tools-menu-btn tools-menu-analyze" id="tools-bar-analyze" title="${tAttr('tools.analyze.aria')}" aria-label="${tAttr('tools.analyze.aria')}" aria-pressed="false" hidden>
      <span class="tools-menu-btn-icon" aria-hidden="true">${ICON_ANALYZE}</span>
      <span class="tools-menu-btn-label">${tHtml('tools.analyze.label')}</span>
    </button>
    <button type="button" class="tools-menu-btn tools-menu-studio" id="tools-bar-studio" title="${tAttr('studio.button.aria')}" aria-label="${tAttr('studio.button.aria')}" aria-pressed="false" hidden>
      <span class="tools-menu-btn-icon" aria-hidden="true">${ICON_STUDIO}</span>
      <span class="tools-menu-btn-label">${tHtml('studio.button.label')}</span>
    </button>
    <button type="button" class="tools-menu-btn tools-menu-toggle" id="tools-menu-toggle" title="${tAttr('tools.toggle.aria')}" aria-label="${tAttr('tools.toggle.aria')}" aria-expanded="false" aria-haspopup="true">
      <span class="tools-menu-btn-icon" aria-hidden="true">${ICON_TOOLS}</span>
      <span class="tools-menu-btn-label">${tHtml('tools.toggle.label')}</span>
    </button>
    <button type="button" class="tools-menu-btn tools-menu-fullscreen" id="tools-menu-fullscreen" title="${tAttr('mapControls.fullscreen.enter')}" aria-label="${tAttr('mapControls.fullscreen.enter')}" aria-pressed="false">
      <span class="tools-menu-btn-icon" aria-hidden="true">${ICON_FULLSCREEN}</span>
    </button>
    <div id="tools-menu-popover" class="tools-menu-popover hidden" role="dialog" aria-modal="false" aria-label="${tAttr('tools.toggle.aria')}">
      <div class="tools-menu-popover-header">
        <span class="tools-menu-popover-title">${tHtml('tools.popover.title')}</span>
        <button type="button" class="tools-menu-close" id="tools-menu-close" aria-label="${tAttr('tools.close.aria')}">&#x2715;</button>
      </div>
      ${onOpenMeeting ? `
      <div class="tools-menu-meeting${meetingAvailable && !meetingAvailable() ? ' hidden' : ''}" id="tools-menu-meeting-section">
        <button type="button" class="tools-menu-meeting-btn" id="tools-menu-meeting">${tHtml('tools.actions.meeting')}</button>
      </div>` : ''}
      ${onOpenSituation ? `
      <div class="tools-menu-meeting${situationAvailable && !situationAvailable() ? ' hidden' : ''}" id="tools-menu-situation-section">
        <button type="button" class="tools-menu-meeting-btn" id="tools-menu-situation">${tHtml('situation.title')}</button>
      </div>` : ''}
      <div class="tools-menu-tabs" role="tablist" aria-label="${tAttr('tools.tabs.aria')}">
        ${tab('layers', 'tools.tab.layers')}${tab('data', 'tools.tab.data')}${tab('view', 'tools.tab.view')}${tab('more', 'tools.tab.more')}
      </div>
      <div class="tools-menu-popover-body">
      <div class="tools-menu-panel" role="tabpanel" id="tools-menu-panel-layers" aria-labelledby="tools-menu-tab-layers" data-accent="layers" hidden>
        <section class="tools-menu-section" id="tools-menu-layers" hidden></section>
        <section class="tools-menu-section" id="tools-menu-satellites" hidden></section>
      </div>
      <div class="tools-menu-panel" role="tabpanel" id="tools-menu-panel-data" aria-labelledby="tools-menu-tab-data" data-accent="data" hidden>
        <section class="tools-menu-section" aria-label="${tAttr('tools.section.data.aria')}">
          <p class="tools-menu-note">${tHtml('tools.data.intro')}</p>
          <div class="tools-data-pins" id="tools-data-pins" role="group"></div>
        </section>
        <section class="tools-menu-section" id="tools-menu-measure" aria-label="${tAttr('tools.measure.aria')}">
          <h4 class="tools-menu-section-title">${tHtml('tools.measure.title')}</h4>
          <p class="tools-menu-note" id="tools-menu-measure-note" hidden></p>
          <div class="tools-measure-row">
            <button type="button" class="tools-measure-btn" id="tools-menu-line">${tHtml('tools.measure.line')}</button>
            <button type="button" class="tools-measure-btn" id="tools-menu-box">${tHtml('tools.measure.box')}</button>
          </div>
          <div class="tools-menu-language-row tools-measure-stats">
            <label for="tools-menu-stats-region" class="sr-only">${tHtml('tools.measure.region')}</label>
            <select id="tools-menu-stats-region" class="tools-menu-language-select" aria-label="${tAttr('tools.measure.region')}">
              <option value="dataset">${tHtml('analyze.scope.dataset')}</option>
              ${(Object.keys(ANALYZE_ZONES) as AnalyzeZoneId[]).map((z) => `<option value="zone:${z}">${tHtml(`analyze.zone.${z}` as MessageKey)}</option>`).join('')}
              <option value="view">${tHtml('analyze.scope.view')}</option>
            </select>
            <button type="button" class="tools-measure-btn" id="tools-menu-stats">${tHtml('tools.measure.stats')}</button>
          </div>
          <p class="tools-menu-note" id="tools-menu-stats-plan" hidden>${tHtml('tools.measure.statsPlan')}</p>
          <div class="tools-menu-subsep" aria-hidden="true"></div>
          ${item('tools-menu-clear', 'tools.actions.clear')}
        </section>
        <section class="tools-menu-section" id="tools-research-record" aria-label="${tAttr('research.record.title')}" hidden></section>
        <section class="tools-menu-section" id="tools-research-lab" aria-label="${tAttr('research.lab.title')}" hidden></section>
      </div>
      <div class="tools-menu-panel" role="tabpanel" id="tools-menu-panel-view" aria-labelledby="tools-menu-tab-view" data-accent="view" hidden>
        <section class="tools-menu-section" id="tools-view-palette" aria-label="${tAttr('tools.palette.aria')}"></section>
        <section class="tools-menu-section" id="tools-view-opacity" aria-label="${tAttr('palette.dialog.transparency')}" hidden></section>
        <section class="tools-menu-section" id="tools-view-layout" aria-label="${tAttr('tools.section.layout.aria')}">
          <h4 class="tools-menu-section-title">${tHtml('tools.section.layout')}</h4>
          <div class="tools-menu-layout-row" role="radiogroup" aria-label="${tAttr('tools.layout.aria')}">
            <button type="button" class="tools-menu-layout-btn${currentLayout === '1' ? ' active' : ''}" id="tools-menu-layout-1" aria-pressed="${currentLayout === '1'}" title="${tAttr('tools.layout.single')}">1</button>
            <button type="button" class="tools-menu-layout-btn${currentLayout === '2h' ? ' active' : ''}" id="tools-menu-layout-2h" aria-pressed="${currentLayout === '2h'}" title="${tAttr('tools.layout.twoHorizontal')}">2&#x2194;</button>
            <button type="button" class="tools-menu-layout-btn${currentLayout === '2v' ? ' active' : ''}" id="tools-menu-layout-2v" aria-pressed="${currentLayout === '2v'}" title="${tAttr('tools.layout.twoVertical')}">2&#x2195;</button>
            <button type="button" class="tools-menu-layout-btn${currentLayout === '4' ? ' active' : ''}" id="tools-menu-layout-4" aria-pressed="${currentLayout === '4'}"${fourGlobeAllowed ? '' : ' aria-disabled="true"'} title="${tAttr(fourGlobeAllowed ? 'tools.layout.four' : 'tools.layout.four.unavailable')}" aria-label="${tAttr(fourGlobeAllowed ? 'tools.layout.four' : 'tools.layout.four.unavailable')}">4</button>
          </div>
        </section>
        <section class="tools-menu-section" aria-label="${tAttr('tools.specular.section.aria')}">
          <h4 class="tools-menu-section-title">${tHtml('tools.specular.section')}</h4>
          <div class="tools-menu-uiscale-row" role="group" aria-label="${tAttr('tools.specular.aria')}">
            <button type="button" class="tools-menu-uiscale-btn${currentSpecular === 'none' ? ' active' : ''}" id="tools-menu-specular-none" aria-pressed="${currentSpecular === 'none'}" data-specular="none">${tHtml('tools.specular.none')}</button>
            <button type="button" class="tools-menu-uiscale-btn${currentSpecular === 'default' ? ' active' : ''}" id="tools-menu-specular-default" aria-pressed="${currentSpecular === 'default'}" data-specular="default">${tHtml('tools.specular.default')}</button>
            <button type="button" class="tools-menu-uiscale-btn${currentSpecular === 'comfortable' ? ' active' : ''}" id="tools-menu-specular-comfortable" aria-pressed="${currentSpecular === 'comfortable'}" data-specular="comfortable">${tHtml('tools.specular.comfortable')}</button>
          </div>
        </section>
        <section class="tools-menu-section" id="tools-view-legend" aria-label="${tAttr('legend.dock.title')}" hidden></section>
      </div>
      <div class="tools-menu-panel" role="tabpanel" id="tools-menu-panel-more" aria-labelledby="tools-menu-tab-more" data-accent="more" hidden>
        <section class="tools-menu-section" aria-label="${tAttr('tools.section.view.aria')}">
          <h4 class="tools-menu-section-title">${tHtml('tools.section.view')}</h4>
          ${item('tools-menu-labels', 'tools.toggles.labels', ' aria-pressed="false"')}
          ${item('tools-menu-terrain', 'tools.toggles.terrain', ' aria-pressed="false"')}
          <button type="button" class="tools-menu-item active" id="tools-menu-info" aria-pressed="true">
            <span class="tools-menu-item-check" aria-hidden="true"></span>
            <span class="tools-menu-item-label">${tHtml('tools.toggles.datasetInfo')}</span>
          </button>
          <button type="button" class="tools-menu-item active" id="tools-menu-legend" aria-pressed="true">
            <span class="tools-menu-item-check" aria-hidden="true"></span>
            <span class="tools-menu-item-label">${tHtml('tools.toggles.legend')}</span>
          </button>
        </section>
        <section class="tools-menu-section" aria-label="${tAttr('tools.uiScale.section.aria')}">
          <h4 class="tools-menu-section-title">${tHtml('tools.uiScale.section')}</h4>
          <div class="tools-menu-uiscale-row" role="group" aria-label="${tAttr('tools.uiScale.aria')}">
            <button type="button" class="tools-menu-uiscale-btn${currentUiScalePreset === 'compact' ? ' active' : ''}" id="tools-menu-uiscale-compact" aria-pressed="${currentUiScalePreset === 'compact'}" data-uiscale="compact">${tHtml('tools.uiScale.compact')}</button>
            <button type="button" class="tools-menu-uiscale-btn${currentUiScalePreset === 'default' ? ' active' : ''}" id="tools-menu-uiscale-default" aria-pressed="${currentUiScalePreset === 'default'}" data-uiscale="default">${tHtml('tools.uiScale.default')}</button>
            <button type="button" class="tools-menu-uiscale-btn${currentUiScalePreset === 'comfortable' ? ' active' : ''}" id="tools-menu-uiscale-comfortable" aria-pressed="${currentUiScalePreset === 'comfortable'}" data-uiscale="comfortable">${tHtml('tools.uiScale.comfortable')}</button>
          </div>
        </section>
        <section class="tools-menu-section" aria-label="${tAttr('tools.section.language.aria')}">
          <h4 class="tools-menu-section-title">${tHtml('tools.section.language')}</h4>
          <div class="tools-menu-language-row">
            <label for="tools-menu-language" class="sr-only">${tHtml('tools.language.aria')}</label>
            <select id="tools-menu-language" class="tools-menu-language-select" aria-label="${tAttr('tools.language.aria')}">
              ${localeOptions}
            </select>
          </div>
        </section>
        ${onOpenOutput ? `
        <section class="tools-menu-section" aria-label="${tAttr('tools.actions.outputWindow')}">
          ${item('tools-menu-output', 'tools.actions.outputWindow')}
          <p class="tools-menu-note">${tHtml('tools.actions.outputWindow.hint')}</p>
          ${onOpenSphereOutput ? item('tools-menu-sphere-output', 'tools.actions.sphereOutput') : ''}
          ${onOpenSphereOutput ? `<p class="tools-menu-note">${tHtml('tools.actions.sphereOutput.hint')}</p>` : ''}
        </section>` : ''}
        <section class="tools-menu-section" aria-label="${tAttr('tools.section.about.aria')}">
          <h4 class="tools-menu-section-title">${tHtml('tools.section.about')}</h4>
          ${onOpenCredits ? item('tools-menu-credits', 'tools.actions.credits') : ''}
          ${item('tools-menu-privacy', 'tools.actions.privacy')}
          <a class="tools-menu-item tools-menu-item-link" id="tools-menu-about" href="/about" target="_blank" rel="noopener">
            <span class="tools-menu-item-check" aria-hidden="true"></span>
            <span class="tools-menu-item-label">${tHtml('tools.actions.aboutPage')}</span>
          </a>
          <a class="tools-menu-item tools-menu-item-link" id="tools-menu-manual" href="/manual" target="_blank" rel="noopener">
            <span class="tools-menu-item-check" aria-hidden="true"></span>
            <span class="tools-menu-item-label">${tHtml('tools.actions.manual')}</span>
          </a>
        </section>
      </div>
      </div>
      ${planLine ? `
      <div class="tools-menu-plan" id="tools-menu-plan">
        <span class="tools-menu-plan-text" id="tools-menu-plan-text"></span>
        <button type="button" class="tools-menu-plan-btn" id="tools-menu-plan-btn"></button>
      </div>` : ''}
    </div>
  `

  // --- Tabs (AYNI) ---
  // A tablist in the WAI-ARIA pattern: one tab in the tab order, the
  // arrow keys (and Home / End) move between them, a click selects.
  selectTab(storedTab())
  const tabButtons = TOOLS_TABS.map((id) => document.getElementById(`tools-menu-tab-${id}`) as HTMLButtonElement)
  tabButtons.forEach((btn, i) => {
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation()
      selectTab(TOOLS_TABS[i])
    })
    btn.addEventListener('keydown', (ev) => {
      const last = TOOLS_TABS.length - 1
      const to = ev.key === 'ArrowRight' ? (i === last ? 0 : i + 1)
        : ev.key === 'ArrowLeft' ? (i === 0 ? last : i - 1)
          : ev.key === 'Home' ? 0 : ev.key === 'End' ? last : null
      if (to === null) return
      ev.preventDefault()
      selectTab(TOOLS_TABS[to], true)
    })
  })
  const planBtn = document.getElementById('tools-menu-plan-btn') as HTMLButtonElement | null
  planBtn?.addEventListener('click', () => {
    closePopoverWhereItCovers()
    planLine?.()?.onClick(document.getElementById('tools-menu-toggle') as HTMLButtonElement)
  })

  const browseBtn = document.getElementById('tools-menu-browse') as HTMLButtonElement
  const toggleBtn = document.getElementById('tools-menu-toggle') as HTMLButtonElement
  const closeBtn = document.getElementById('tools-menu-close') as HTMLButtonElement

  browseBtn.addEventListener('click', (ev) => {
    ev.stopPropagation()
    closePopoverWhereItCovers()
    onOpenBrowse?.()
  })

  document.getElementById('tools-menu-solar')?.addEventListener('click', (ev) => {
    ev.stopPropagation()
    closePopoverWhereItCovers()
    onOpenSolar?.()
  })

  toggleBtn.addEventListener('click', (ev) => {
    ev.stopPropagation()
    if (isOpen) {
      closePopover()
    } else {
      openPopover()
    }
  })

  closeBtn.addEventListener('click', (ev) => {
    ev.stopPropagation()
    closePopover()
  })

  // Fullscreen toggle — Plan §3.3. Uses the Fullscreen API.
  // `fullscreenchange` keeps the icon /
  // label in sync when the visitor exits via Escape or the
  // browser chrome.
  const fullscreenBtn = document.getElementById('tools-menu-fullscreen') as HTMLButtonElement | null
  fullscreenBtn?.addEventListener('click', (ev) => {
    ev.stopPropagation()
    // AYNI: fullscreen changes the window, not what is open in it.
    void toggleFullscreen()
  })
  // Adopted before the first sync, so the button's initial label
  // reflects a window the kiosk flag or a restored preference may
  // already have made fullscreen.
  controller = callbacks.fullscreen ?? null
  // Through the controller when there is one: F11 and the kiosk flag
  // change the *native* window without firing `fullscreenchange`, so
  // the DOM event alone would leave the button stale after either.
  controller?.onChange(syncFullscreenButton)
  if (!document.body.dataset.toolsMenuFullscreenWired) {
    document.body.dataset.toolsMenuFullscreenWired = 'true'
    document.addEventListener('fullscreenchange', syncFullscreenButton)
  }
  syncFullscreenButton()

  // Outside click closes the popover. We look up #map-controls
  // inside the handler rather than closing over the `container`
  // reference so re-init flows (tests, hot reload) don't leave
  // stale listeners pointing at detached DOM nodes.
  if (!document.body.dataset.toolsMenuListenersWired) {
    document.body.dataset.toolsMenuListenersWired = 'true'
    document.addEventListener('click', (ev) => {
      if (!isOpen) return
      const target = ev.target as Node | null
      if (!target) return
      const host = document.getElementById('map-controls')
      if (host && host.contains(target)) return
      // AYNI: a control that re-renders its section on click (a Layers card
      // opening its list) has left the page by the time the click gets
      // here, so `contains` says "outside" and a phone lost the whole panel.
      // The path was recorded when the click happened.
      if (host && ev.composedPath().includes(host)) return
      closePopoverWhereItCovers()
    })
    // Escape closes the popover. Stop propagation so other handlers
    // (the Khipu panel, chat) don't interpret the same keypress.
    document.addEventListener('keydown', (ev) => {
      if (!isOpen) return
      if (ev.key === 'Escape') {
        // AYNI: the popover now stays open behind a dialog opened from
        // it. Escape pressed in that dialog (or in the browse panel's
        // search box) is for whatever has the focus, not for the menu.
        const focused = document.activeElement
        const host = document.getElementById('map-controls')
        if (focused && focused !== document.body && host && !host.contains(focused)) return
        ev.stopPropagation()
        closePopover()
        const toggle = document.getElementById('tools-menu-toggle') as HTMLButtonElement | null
        toggle?.focus()
      }
    })
    // Keep the Tools bar positioned above the playback transport
    // on window resize / orientation change.
    window.addEventListener('resize', updateMapControlsPosition)
  }

  // --- View toggles ---

  const labelsBtn = document.getElementById('tools-menu-labels') as HTMLButtonElement
  const terrainBtn = document.getElementById('tools-menu-terrain') as HTMLButtonElement
  const infoBtn = document.getElementById('tools-menu-info') as HTMLButtonElement
  const legendBtn = document.getElementById('tools-menu-legend') as HTMLButtonElement
  const clearBtn = document.getElementById('tools-menu-clear') as HTMLButtonElement
  // --- Language picker ---
  // Persists the choice and reloads — vanilla TS has no per-module
  // re-render hook, so reload is the sanctioned UX (see docs/archive/terraviz/I18N_PLAN.md).
  const languageSelect = document.getElementById('tools-menu-language') as HTMLSelectElement | null
  languageSelect?.addEventListener('change', () => {
    const next = languageSelect.value as Locale
    saveLocalePref(next)
    window.location.reload()
  })

  // --- UI-size radio (§7.1) ---
  // Three discrete presets — Compact / Default / Comfortable. The
  // service writes to :root + localStorage; we only mirror the
  // active class so the radio's visual state stays in sync.
  const uiScaleButtons: Array<{ btn: HTMLButtonElement; preset: UiScalePreset }> = []
  for (const preset of Object.keys(UI_SCALE_PRESETS) as UiScalePreset[]) {
    const btn = document.getElementById(`tools-menu-uiscale-${preset}`) as HTMLButtonElement | null
    if (btn) uiScaleButtons.push({ btn, preset })
  }
  for (const { btn, preset } of uiScaleButtons) {
    btn.addEventListener('click', () => {
      setUiScale(UI_SCALE_PRESETS[preset])
      for (const other of uiScaleButtons) {
        const active = other.preset === preset
        other.btn.classList.toggle('active', active)
        other.btn.setAttribute('aria-pressed', String(active))
      }
      emitSetting('ui_scale', preset)
      announce?.(t('tools.uiScale.announce', { label: t(`tools.uiScale.${preset}`) }))
    })
  }

  // --- Specular preset radio (§7.2) ---
  // Three discrete presets — None / Default / Comfortable mirror the
  // plan-fixed names. The service writes the value into the shader-
  // settings snapshot + localStorage and fires a change event that
  // the earth-tile layer subscribes to via triggerRepaint, so the
  // glint updates on the next frame without waiting for camera motion.
  const specularButtons: Array<{ btn: HTMLButtonElement; preset: SpecularPreset }> = []
  for (const preset of Object.keys(SPECULAR_PRESETS) as SpecularPreset[]) {
    const btn = document.getElementById(`tools-menu-specular-${preset}`) as HTMLButtonElement | null
    if (btn) specularButtons.push({ btn, preset })
  }
  for (const { btn, preset } of specularButtons) {
    btn.addEventListener('click', () => {
      setSpecularPreset(preset)
      for (const other of specularButtons) {
        const active = other.preset === preset
        other.btn.classList.toggle('active', active)
        other.btn.setAttribute('aria-pressed', String(active))
      }
      emitSetting('specular', preset)
      announce?.(t('tools.specular.announce', { label: t(`tools.specular.${preset}`) }))
    })
  }

  labelsBtn.addEventListener('click', () => {
    // Target state is derived from the button class, not from any
    // renderer's reported state — newly-created siblings may still
    // be loading their style when the click fires. MapLibre queues
    // layer operations internally until the style is ready.
    const next = !labelsBtn.classList.contains('active')
    for (const r of viewports.getAll()) r.toggleLabels?.(next)
    setButtonState(labelsBtn, next)
    emitSetting('labels', next ? 'on' : 'off')
    announce?.(t(next ? 'tools.announce.labels.on' : 'tools.announce.labels.off'))
  })

  terrainBtn.addEventListener('click', () => {
    const next = !terrainBtn.classList.contains('active')
    for (const r of viewports.getAll()) {
      // toggleTerrain is not on the GlobeRenderer interface yet — cast
      // through the underlying MapRenderer which implements it.
      ;(r as unknown as { toggleTerrain?: (v: boolean) => void }).toggleTerrain?.(next)
    }
    setButtonState(terrainBtn, next)
    emitSetting('terrain', next ? 'on' : 'off')
    announce?.(t(next ? 'tools.announce.terrain.on' : 'tools.announce.terrain.off'))
  })

  infoBtn.addEventListener('click', () => {
    const next = !infoBtn.classList.contains('active')
    setButtonState(infoBtn, next)
    onToggleDatasetInfo?.(next)
    emitSetting('dataset_info', next ? 'on' : 'off')
    announce?.(t(next ? 'tools.announce.datasetInfo.on' : 'tools.announce.datasetInfo.off'))
  })

  legendBtn.addEventListener('click', () => {
    const next = !legendBtn.classList.contains('active')
    setButtonState(legendBtn, next)
    onToggleLegend?.(next)
    emitSetting('legend', next ? 'on' : 'off')
    announce?.(t(next ? 'tools.announce.legend.on' : 'tools.announce.legend.off'))
  })

  // AYNI: everything on the globe that a person or the guide put there —
  // pins, the line, the box and its outline, isolines, the guide's
  // markers and highlighted regions. Analysis first: its own annotations
  // are highlights too, and it keeps state that must go with them.
  clearBtn.addEventListener('click', () => {
    clearAnalysis()
    callbacks.onClearPins?.()
    for (const r of viewports.getAll()) {
      r.clearMarkers?.()
      ;(r as unknown as { clearHighlights?: () => void }).clearHighlights?.()
    }
    announce?.(t('tools.announce.cleared'))
  })

  // --- Measure (AYNI): line, box and region statistics, shown in the Analyze panel ---
  const lineBtn = document.getElementById('tools-menu-line') as HTMLButtonElement | null
  const boxBtn = document.getElementById('tools-menu-box') as HTMLButtonElement | null
  const statsBtn = document.getElementById('tools-menu-stats') as HTMLButtonElement | null
  lineBtn?.addEventListener('click', () => {
    closePopoverWhereItCovers()
    startAnalyzeLine(toggleBtn)
    announce?.(t('tools.announce.lineStarted'))
  })
  boxBtn?.addEventListener('click', () => {
    closePopoverWhereItCovers()
    startAnalyzeBox(toggleBtn)
    announce?.(t('tools.announce.boxStarted'))
  })
  // AYNI (owner, 2026-10-06): Analyze one click from the bar, on the region last chosen.
  document.getElementById('tools-bar-analyze')?.addEventListener('click', (ev) => {
    const btn = ev.currentTarget as HTMLElement
    if (isAnalyzeUIOpen()) {
      closeAnalyzeUI()
    } else {
      closePopoverWhereItCovers()
      openAnalyzeScoped(statsScope(), btn)
      announce?.(t('tools.announce.analyzeOpened'))
    }
    syncAnalyzeButton()
  })
  statsBtn?.addEventListener('click', () => {
    closePopoverWhereItCovers()
    setAnalyzeTab('stats')
    openAnalyzeScoped(statsScope(), toggleBtn)
    announce?.(t('tools.announce.analyzeOpened'))
  })

  // AYNI (multi-globe plan, phase 4): the output window, rendered only when wired.
  if (onOpenOutput) {
    document.getElementById('tools-menu-output')?.addEventListener('click', () => {
      const result = onOpenOutput()
      announce?.(t(result === 'opened' ? 'tools.announce.outputWindow' : 'tools.announce.outputWindowBlocked'))
    })
  }
  if (onOpenSphereOutput) {
    document.getElementById('tools-menu-sphere-output')?.addEventListener('click', () => {
      const result = onOpenSphereOutput()
      announce?.(t(result === 'opened' ? 'tools.announce.sphereOutput' : 'tools.announce.outputWindowBlocked'))
    })
  }

  // Credits button is only rendered when `onOpenCredits` is wired
  // (see template above). Skipping the listener entirely when the
  // callback is absent means there's no dead control in the DOM —
  // the About section just shows Privacy.
  if (onOpenCredits) {
    const creditsBtn = document.getElementById('tools-menu-credits') as HTMLButtonElement | null
    creditsBtn?.addEventListener('click', () => {
      closePopoverWhereItCovers()
      // Pass the Tools toggle button (which stays visible as the
      // popover's anchor) as the credits panel's focus-restore
      // target. The menu item itself is hidden by closePopover()
      // above, so it can't reliably receive focus on close.
      onOpenCredits(toggleBtn)
      announce?.(t('tools.announce.creditsOpened'))
    })
  }

  if (onOpenMeeting) {
    const meetingBtn = document.getElementById('tools-menu-meeting') as HTMLButtonElement | null
    meetingBtn?.addEventListener('click', () => {
      closePopoverWhereItCovers()
      // The toggle button, not the menu item: see Credits above.
      onOpenMeeting(toggleBtn)
      announce?.(t('tools.announce.meetingOpened'))
    })
  }

  if (onOpenSituation) {
    document.getElementById('tools-menu-situation')?.addEventListener('click', () => {
      closePopoverWhereItCovers()
      onOpenSituation(toggleBtn)
    })
  }

  const privacyBtn = document.getElementById('tools-menu-privacy') as HTMLButtonElement | null
  privacyBtn?.addEventListener('click', () => {
    closePopoverWhereItCovers()
    openPrivacyUI(privacyBtn)
    announce?.(t('tools.announce.privacyOpened'))
  })

  // --- Layout picker (Tools → View; the plan gates four globes) ---

  if (onSetLayout) {
    const layouts: ViewLayout[] = ['1', '2h', '2v', '4']
    const layoutBtns = new Map<ViewLayout, HTMLButtonElement>()
    for (const l of layouts) {
      const btn = document.getElementById(`tools-menu-layout-${l}`) as HTMLButtonElement | null
      if (btn) layoutBtns.set(l, btn)
    }
    for (const [layout, btn] of layoutBtns) {
      btn.addEventListener('click', () => {
        // `aria-disabled` keeps the control focusable so its reason is
        // reachable, which means the click still arrives and has to be
        // refused here. Silently: the label already says why, and
        // announcing a refusal on every press would nag.
        // AYNI: a layout the plan locks opens the plans and says why.
        if (btn.dataset.planLock === '1') {
          openPlans(toggleBtn, 'research', { notice: t('plans.locked.globes') })
          return
        }
        if (btn.getAttribute('aria-disabled') === 'true') return
        onSetLayout(layout)
        for (const [l, b] of layoutBtns) {
          const active = l === layout
          b.classList.toggle('active', active)
          b.setAttribute('aria-pressed', String(active))
        }
        announce?.(t('tools.announce.layout', { label: layoutLabel(layout) }))
      })
    }
  }

}

/**
 * AYNI (owner, 2026-10-09): what the panel shows for the account — Start a
 * meeting, the plan line and the plan's locks — drawn again when the account
 * changes. On a computer Tools can be open before the licence has loaded,
 * and Start a meeting then stayed hidden until the panel was closed and
 * opened again.
 */
export function refreshToolsAccount(): void {
  if (meetingAvailable) {
    document.getElementById('tools-menu-meeting-section')?.classList.toggle('hidden', !meetingAvailable())
  }
  if (situationAvailable) {
    document.getElementById('tools-menu-situation-section')?.classList.toggle('hidden', !situationAvailable())
  }
  renderPlanLine()
  renderPlanGates()
}

/** Open the popover and focus its close button for keyboard users. */
function openPopover(): void {
  const popover = document.getElementById('tools-menu-popover')
  const toggle = document.getElementById('tools-menu-toggle')
  if (!popover || !toggle) return
  if (meetingAvailable) {
    document.getElementById('tools-menu-meeting-section')?.classList.toggle('hidden', !meetingAvailable())
  }
  if (situationAvailable) {
    document.getElementById('tools-menu-situation-section')?.classList.toggle('hidden', !situationAvailable())
  }
  renderPlanLine()
  renderMeasureNote()
  renderPlanGates()
  popover.classList.remove('hidden')
  toggle.setAttribute('aria-expanded', 'true')
  isOpen = true
  const close = document.getElementById('tools-menu-close') as HTMLButtonElement | null
  close?.focus()
}

/**
 * AYNI: close the popover only where it would be in the way.
 *
 * On a desktop window the popover is a panel beside the globe: the
 * globe, the corner buttons and the browse panel all make room for it
 * (`globePanelOffset.ts`), so it stays until its own button, its ✕ or
 * Escape closes it — choosing an option, opening a dialog from it or
 * clicking elsewhere leaves it as it is. On a narrow window it covers
 * most of the screen, and whatever was just opened would be under it.
 */
function closePopoverWhereItCovers(): void {
  if (window.innerWidth <= 768) closePopover()
}

/** Close the popover. */
function closePopover(): void {
  const popover = document.getElementById('tools-menu-popover')
  const toggle = document.getElementById('tools-menu-toggle')
  if (!popover || !toggle) return
  popover.classList.add('hidden')
  toggle.setAttribute('aria-expanded', 'false')
  isOpen = false
}

/**
 * Close the popover from outside the menu: `globePanelOffset.ts` does
 * when the browse panel has just opened and the window cannot hold both.
 */
/** AYNI: the Solar System button's pressed look, while the orrery shows. */
export function syncSolarButton(pressed: boolean): void {
  document.getElementById('tools-menu-solar')?.setAttribute('aria-pressed', String(pressed))
}

export function closeToolsMenu(): void {
  if (isOpen) closePopover()
}

/** Whether the popover is currently open — used by tests. */
export function isToolsMenuOpen(): boolean {
  return isOpen
}

/**
 * Briefly pulse the Browse button to draw the user's attention.
 * Called by main.ts after init on mobile (where the browse panel
 * starts closed so the globe is visible) so first-time users notice
 * where datasets live.
 *
 * The animation is a gentle halo that radiates outward for ~1.2s
 * per cycle and runs two cycles total. Tapping the button cancels
 * the animation immediately so the pulse doesn't keep drawing
 * attention after the user has already engaged. Respects
 * `prefers-reduced-motion` — users who've opted out see no
 * animation, just the button in its normal state.
 */
export function pulseBrowseButton(): void {
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return
  }
  const btn = document.getElementById('tools-menu-browse')
  if (!btn) return
  btn.classList.add('pulse-attention')
  const clearPulse = () => {
    btn.classList.remove('pulse-attention')
    btn.removeEventListener('click', clearPulse)
  }
  // Clear when the animation finishes naturally (2 cycles × 1.2s)
  window.setTimeout(clearPulse, 2600)
  // Or immediately if the user taps the button before it finishes
  btn.addEventListener('click', clearPulse, { once: true })
}

/** Sync a toggle button's `.active` class + aria-pressed to a bool. */
function setButtonState(btn: HTMLElement, active: boolean): void {
  btn.classList.toggle('active', active)
  btn.setAttribute('aria-pressed', String(active))
}

/**
 * Sync the toolbar button states to explicit values. Called by
 * main.ts after goHome, layout changes, or when loading
 * persisted view preferences so the toolbar reflects the actual
 * renderer + preferences state.
 */
export function syncToolsMenuState(state: {
  labels?: boolean
  /** AYNI: no longer shown — borders are an overlay (Layers); callers still pass it. */
  borders?: boolean
  terrain?: boolean
  /** AYNI: no longer shown here — the auto-rotate button under Help reads the globe itself. */
  autoRotate?: boolean
  datasetInfo?: boolean
  legend?: boolean
}): void {
  if (state.labels !== undefined) {
    const btn = document.getElementById('tools-menu-labels')
    if (btn) setButtonState(btn, state.labels)
  }
  if (state.terrain !== undefined) {
    const btn = document.getElementById('tools-menu-terrain')
    if (btn) setButtonState(btn, state.terrain)
  }
  if (state.datasetInfo !== undefined) {
    const btn = document.getElementById('tools-menu-info')
    if (btn) setButtonState(btn, state.datasetInfo)
  }
  if (state.legend !== undefined) {
    const btn = document.getElementById('tools-menu-legend')
    if (btn) setButtonState(btn, state.legend)
  }
}

/**
 * Sync the layout picker buttons to the given layout. Called by
 * main.ts when a layout change comes from a non-UI path (a deep link,
 * URL init, `setEnvView` API) so the picker buttons stay in sync.
 */
export function syncToolsMenuLayout(layout: ViewLayout): void {
  const layouts: ViewLayout[] = ['1', '2h', '2v', '4']
  for (const l of layouts) {
    const btn = document.getElementById(`tools-menu-layout-${l}`)
    if (btn) {
      const active = l === layout
      btn.classList.toggle('active', active)
      btn.setAttribute('aria-pressed', String(active))
    }
  }
}

/** Human-readable label for a layout value, used in announcements. */
function layoutLabel(layout: ViewLayout): string {
  switch (layout) {
    case '1': return t('tools.layout.single')
    case '2h': return t('tools.layout.twoHorizontal')
    case '2v': return t('tools.layout.twoVertical')
    case '4': return t('tools.layout.four')
  }
}
